import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "20";
const height = "20";
const viewBox = "0 0 20 20";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    small: { width: 17, height: 17 },
    medium: { width: 24, height: 24 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M17.622 0H2.377A2.375 2.375 0 00-.001 2.372v15.256A2.375 2.375 0 002.377 20h15.245a2.374 2.374 0 002.377-2.372V2.372A2.375 2.375 0 0017.621 0zm-3.729 2.838c0-.308.25-.558.559-.558h2.704c.309 0 .559.25.559.558v2.697c0 .309-.25.558-.559.558h-2.704a.558.558 0 01-.559-.558V2.838zm-3.87 3.348c2.124 0 3.846 1.718 3.846 3.838s-1.722 3.837-3.846 3.837-3.846-1.718-3.846-3.837 1.722-3.838 3.846-3.838zm7.739 10.93c0 .308-.25.558-.559.558H2.844a.558.558 0 01-.559-.558V8.418h1.871a6.025 6.025 0 00-.216 1.605c0 3.346 2.729 6.07 6.084 6.07s6.084-2.723 6.084-6.07c0-.555-.076-1.093-.216-1.605h1.871v8.698z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Instagram"
});
